/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import { useWeb3React } from "@web3-react/core";

// Utils
import { getConnector } from "utils/connectors";
import { getLocalStorageValue } from "utils/common";

// Types
import { NetworkConfigType } from "config/network/types";
import { InjectedConnector } from "@web3-react/injected-connector";
import { ACTIVATE_FAILED, ACTIVATE_SUCCESS } from "constants/analytics";

const useEagerConnect = (
  config: NetworkConfigType,
  actionPref: string,
  connectionPref: string,
  selectedChain: string,
  setConnectorPref: any,
  isLedgerApp: boolean,
  isCheckingLedger: boolean,
  track: any
) => {
  const { activate, active } = useWeb3React();

  const [tried, setTried] = useState(false);

  const injectedActivation = useCallback(
    async (key: string) => {
      const injectedConnector = getConnector(key, config) as InjectedConnector;
      const isAuthorized = await injectedConnector?.isAuthorized();
      const chainId = await injectedConnector.getChainId();

      if (isAuthorized && +chainId === +selectedChain) {
        try {
          activate(injectedConnector, undefined, true);
          setConnectorPref(key);
        } catch (e) {
          setTried(true);
        }
      } else {
        setTried(true);
      }
    },
    [selectedChain, config, activate, setConnectorPref]
  );

  useEffect(() => {
    if (!isCheckingLedger && isLedgerApp) {
      const connectorKey = "ledgerLive";
      const ledgerLiveConnector = getConnector(connectorKey, config);

      setTimeout(() => {
        activate(ledgerLiveConnector, (error) => {
          track(ACTIVATE_FAILED, { wallet_name: connectorKey });
          throw error;
        }).then(() => {
          track(ACTIVATE_SUCCESS);
          setConnectorPref(connectorKey);
        });
      }, 1000);
    }
  }, [isLedgerApp, isCheckingLedger, config]);

  useEffect(() => {
    if (actionPref === "CONNECT" && !isCheckingLedger && !isLedgerApp) {
      (async function connect() {
        if (connectionPref === "walletConnect") {
          const walletConnectConnector = getConnector("walletConnect", config);
          const walletInfoLC = getLocalStorageValue("walletconnect");

          if (walletInfoLC) {
            try {
              setTimeout(() => {
                activate(walletConnectConnector, undefined, true);
                setConnectorPref("walletConnect");
              }, 1000);
            } catch (e) {
              console.log(e);
              setTried(true);
            } finally {
              setTried(true);
            }
          }
        } else if (connectionPref === "coinbaseWallet") {
          const walletLinkConnector = getConnector("coinbaseWallet", config);
          const walletInfoLC = getLocalStorageValue(
            "-walletlink:https://www.walletlink.org:walletUsername"
          );
          if (walletInfoLC) {
            try {
              setTimeout(() => {
                activate(walletLinkConnector, undefined, true);
                setConnectorPref("coinbaseWallet");
              }, 1000);
            } catch (e) {
              console.log(e);
              setTried(true);
            } finally {
              setTried(true);
            }
          }
        } else if (connectionPref === "ledger") {
          const ledgerConnector = getConnector("ledger", config);
          try {
            setTimeout(() => {
              activate(ledgerConnector, undefined, true);
              setConnectorPref("ledger");
            }, 1000);
          } catch (e) {
            console.log(e);
            setTried(true);
          } finally {
            setTried(true);
          }
        } else {
          if (!connectionPref || connectionPref === "ledgerLive") {
            return;
          }

          injectedActivation(connectionPref);
        }
      })();
    }
  }, [isLedgerApp, isCheckingLedger]);

  useEffect(() => {
    if (!tried && active) {
      setTried(true);
    }
  }, [tried, active]);

  return tried;
};

export default useEagerConnect;
